import { useEffect, useRef, useState } from "react";

import useDataContext from "../providers/DataProvider/useDataContext";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import Whatsapp from "../components/Whatsapp/Whatsapp";
import RoomCard from "../components/RoomCard/RoomCard";
import AutoSlider from "../components/AutoSlider/AutoSlider";
import Spinner from "../components/Spinner/Spinner";
import InstagramButton from "../components/InstagramButton/InstagramButton";
import OutsideCard from "../components/OutsideCard/OutsideCard";

const HomePage: React.FC = () => {
    const { data } = useDataContext();
    const [onToggleMenu, setOnToggleMenu] = useState(false);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const iframeContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth"});
    }, []);


    /* Agregar el iframe de Google Maps luego de que se monte el componente. Esto es 
       necesario solo por un Minifed React Error de hidratación que se causaba cuando 
       hacia la build de las páginas estáticas con ViteReactSSG */
    useEffect(() => {
        if (iframeContainerRef.current) {
            const iframe = document.createElement("iframe");
            iframe.setAttribute("aria-label", "location-iframe");
            iframe.setAttribute("width", "100%");
            iframe.setAttribute("height", "300");
            iframe.setAttribute("frameBorder", "0");
            iframe.setAttribute("scrolling", "no");
            iframe.setAttribute("marginHeight", "0");
            iframe.setAttribute("marginWidth", "0");
            iframe.setAttribute("src", "https://maps.google.com/maps?width=100%25&height=300&hl=en&q=Juan%20Manuel%20de%20Rosas%20589,%20R8400%20San%20Carlos%20de%20Bariloche,%20R%C3%ADo%20Negro+(Lake%20Inn)&t=&z=15&ie=UTF8&iwloc=B&output=embed");
            iframe.setAttribute("title", data.pages.homePage.location.iframeTitle);
            iframe.onload = () => setIsIframeLoading(false);
            iframeContainerRef.current.appendChild(iframe);
            return () => {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                iframeContainerRef.current?.removeChild(iframe);
            };
        }
    }, [data.pages.homePage.location.iframeTitle]); 

    return (
    <>
        <section aria-label="hero-section"
                 className="min-h-screen relative flex flex-col
                            before:content-[''] before:absolute before:size-full 
                            before:top-0 before:left-0 before:backdrop-brightness-50 
                            before:z-10">

            <Header isHomePage onMenuToggle={setOnToggleMenu}/>

            <div className="h-screen">
                <AutoSlider images={data.pages.homePage.hero.sliderImages}/>
            </div>

            <div className="grid grid-cols-1 py-4 absolute top-0 bottom-0 w-full z-20">
                <figure className={`mt-auto mx-auto transition-opacity duration-300 ease-out ${onToggleMenu ? "opacity-0" : "opacity-100"}` }>
                    <img src={data.pages.homePage.hero.fullLogo.src}
                         alt={data.pages.homePage.hero.fullLogo.alt}/>
                </figure>
                <a href={ data.pages.bookPage.externalUrl }
                   target="_blank"
                   rel="noopener noreferrer"
                   className="place-self-end mx-auto mb-14 lg:mb-4 bg-transparent border border-forth-solid
                              rounded-md px-12 py-5 text-fifth-solid text-xl
                              transition duration-500 ease-out
                            hover:bg-first-solid">
                    { data.pages.homePage.hero.bookButton.name }
                </a>
            </div>
        </section>

        <main className="text-zinc-900">
            <section aria-label="presentation-section"
                     className="bg-fifth-solid py-4">
                <div className="container mx-auto flex flex-col gap-5 px-4">
                    <h1 className="text-3xl lg:text-4xl font-bold lg:py-1 text-center">
                        { data.pages.homePage.presentation.title }
                    </h1>
                    <h2 className="text-lg text-center lg:text-3xl font-bold">
                        { data.pages.homePage.presentation.subtitle }
                    </h2>
                    <div className="flex flex-col gap-4 lg:gap-3">
                        { data.pages.homePage.presentation.paragraphs.map((paragraph, index) => (
                            <p key={index}
                            className="text-base/10 text-justify lg:text-lg/10 font-normal">
                                { paragraph }
                            </p>
                        ))}
                    </div>
                </div>
            </section>

            <section aria-label="rooms-section"
                     className="bg-forth-solid py-8">
                <div className="px-4 container mx-auto flex flex-col gap-8">
                    <h3 className="text-2xl text-center lg:text-left lg:text-3xl 
                                   font-medium special-title">
                        { data.pages.homePage.rooms.title }
                    </h3>

                    <ul role="list"
                        className="grid grid-cols-1 gap-y-8 gap-x-8 md:grid-cols-2 lg:grid-cols-3">
                    {
                        data.rooms.map((room) => (
                            <li role="listitem"
                                key={room.name}
                                className="h-full">
                                <RoomCard room={room}/>
                            </li>
                        ))
                    }
                    </ul>
                </div>
            </section>

            <section aria-label="outside-section"
                     className="bg-third-solid py-8">
                <div className="px-4 container mx-auto flex flex-col gap-8">
                    <h3 className="text-2xl lg:text-3xl text-center font-medium 
                                   special-title">
                        { data.pages.homePage.outside.title }
                    </h3>
                    <ul role="list"
                        className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 gap-x-8">
                    {
                        data.outside.map((out) => (
                            <li role="listitem"
                                key={out.index}
                                className="h-full">
                                <OutsideCard outsideCardImage={out.images.cardImage}
                                             outsideIndex={out.index}
                                             outsideName={out.name}/>
                            </li>
                        ))
                    }
                    </ul>
                </div>
            </section>

            <section aria-label="location-section"
                     className="bg-forth-solid">
                <div className="container mx-auto px-4 py-8 flex flex-col gap-8">
                    <h3 className="text-2xl lg:text-3xl text-center lg:text-right font-medium special-title">
                        { data.pages.homePage.location.title }
                    </h3>
                    <div className="border-4 border-first-solid/50 rounded-2xl overflow-hidden h-[300px]"
                         ref={iframeContainerRef}>
                    { 
                        isIframeLoading && <Spinner color="bg-first-solid" pxSize={20} height="full"/> 
                    }
                    </div>
                </div>
            </section>

        </main>

        <InstagramButton isHomePage/>

        <Whatsapp isHomePage/>

        <Footer isHomePage/>
    </>
    )
}

export default HomePage;
